import { create } from 'zustand';
import { Vector3, Mesh } from 'three';
import useEditorStore from './useEditorStore';

const ids = ['subPatellar', 'midPatellar', 'frontEnd', 'internalCondyle', 'externalCondyle'];

const useAxesAlignmentStore = create((set, get) => ({
  // Stores each point selection with sphere mesh, and completion flag
  selections: {
    subPatellar:      { sphere: null, completed: false },
    midPatellar:      { sphere: null, completed: false },
    frontEnd:         { sphere: null, completed: false },
    internalCondyle:  { sphere: null, completed: false },
    externalCondyle:  { sphere: null, completed: false },
  },

  // Which key is currently active for placement (ignored if already completed)
  activeKey: null,

  // Activate a selection key (prepares for raycasting)
  setActiveKey: (key) => {
    const { selections } = get();
    if (key && selections[key].completed) return;  // skip completed
    set({ activeKey: key });
  },

  // Save the clicked point and sphere, mark completed, then clear activeKey
  savePoint: (key, sphere) => {
    set((state) => ({
      selections: {
        ...state.selections,
        [key]: { sphere, completed: true },
      },
    }));

    // Find the index of the current key
    const currentIndex = ids.indexOf(key);

    // Find the next available ID that is not yet completed
    let nextIndex = -1;
    for (let i = currentIndex + 1; i < ids.length; i++) {
      if (!get().selections[ids[i]].completed) {
        nextIndex = i;
        break;
      }
    }

    // If a next key exists, set it as activeKey
    if (nextIndex !== -1) {
      set({ activeKey: ids[nextIndex] });
    } else {
      set({ activeKey: null });
    }
  },

  // Reset a single selection
  resetPoint: (key) => {
    // dispose existing sphere mesh to avoid memory leaks
    const { sceneRefs } = useEditorStore.getState();
    const mesh = get().selections[key].sphere;
    if (mesh && sceneRefs.shadedMesh) {
      sceneRefs.shadedMesh.remove(mesh);
      if(mesh.geometry) mesh.geometry.dispose();
      if(mesh.material) mesh.material.dispose();
    }
    
    // reset the selection state
    set((state) => ({
      selections: {
        ...state.selections,
        [key]: { sphere: null, completed: false },
      },
    }));
  },

  // Clear all selections and activeKey
  clearSelections: () => {
    set({
      selections: {
        subPatellar:      { sphere: null, completed: false },
        midPatellar:      { sphere: null, completed: false },
        frontEnd:         { sphere: null, completed: false },
        internalCondyle:  { sphere: null, completed: false },
        externalCondyle:  { sphere: null, completed: false },
      },
      activeKey: null,
    });
  },

  // Selector to check if all selections are completed
  areAllSelectionsCompleted: () => {
    const { selections } = get();
    return Object.values(selections).every(selection => selection.completed);
  },

  isAligned: false,
  setAlignmentStatus: (status) => set({isAligned : status}),
}));

export default useAxesAlignmentStore;
